import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { auditAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, ModernButton, ModernInput, ModernSelect, LoadingSpinner, Badge } from '../components/SharedComponents';
import {
  FiFileText, FiActivity, FiTrendingUp, FiSearch, FiRefreshCw, FiUser, FiZap,
  FiFilter, FiClock, FiAlertCircle,
} from '../components/Icons';

/* ------------------------------------------------------------------ *
 *  Read-only audit-log viewer. Fixes over the previous version:       *
 *  - styles no longer reference theme keys that don't exist           *
 *  - date filters are sent as ISO-with-timezone (created_at is        *
 *    timestamptz; naive local strings shifted results by UTC offset)  *
 *  - pagination (the API supports offset/limit; the UI never did)     *
 *  - expandable row detail (old/new values, endpoint, error, agent)   *
 *  - summary period selector (backend accepts 1-365 days)             *
 * ------------------------------------------------------------------ */

const PAGE_SIZES = [50, 100, 200, 500];
const SUMMARY_PERIODS = [7, 30, 90];

// Single-hue distribution bars (magnitude, not identity).
const BAR_HUE = '#3a6ea5';

const prettyAction = (a) => (a || '').replace(/_/g, ' ');

/* ----------------------------- styles ----------------------------- */

const PageContainer = styled.div`
  width: 100%;
  margin: 0;
  padding: ${(p) => p.theme.spacing.lg};
  box-sizing: border-box;
  max-width: none;
`;

const HeaderCard = styled(Card)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${(p) => p.theme.spacing.lg};
  flex-wrap: wrap;
  margin-bottom: ${(p) => p.theme.spacing.xl};

  h1 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: ${(p) => p.theme.spacing.sm};
  }

  p {
    margin: ${(p) => p.theme.spacing.xs} 0 0 0;
    color: ${(p) => p.theme.colors.text.secondary};
    font-size: 0.95rem;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  gap: ${(p) => p.theme.spacing.md};
  align-items: flex-end;
  flex-wrap: wrap;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.colors.text.secondary};
  }
`;

const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid ${(p) => p.theme.colors.border.medium};
  border-radius: ${(p) => p.theme.borderRadius.medium};
  overflow: hidden;

  button {
    border: none;
    background: ${(p) => p.theme.colors.surface};
    color: ${(p) => p.theme.colors.text.secondary};
    padding: 8px 14px;
    font-size: 0.88rem;
    font-weight: 600;
    font-family: ${(p) => p.theme.fonts.main};
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    & + button {
      border-left: 1px solid ${(p) => p.theme.colors.border.medium};
    }

    &.active {
      background: ${(p) => p.theme.colors.primary};
      color: ${(p) => p.theme.colors.text.inverse};
    }
  }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${(p) => p.theme.spacing.md};
  margin-bottom: ${(p) => p.theme.spacing.xl};
`;

const StatCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .value {
    font-size: 1.9rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.1;
    color: ${(p) => p.accent || p.theme.colors.text.primary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .label {
    font-size: 0.82rem;
    font-weight: 600;
    color: ${(p) => p.theme.colors.text.secondary};
  }

  .sub {
    font-size: 0.75rem;
    color: ${(p) => p.theme.colors.text.light};
  }
`;

const DistGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: ${(p) => p.theme.spacing.md};
  margin-bottom: ${(p) => p.theme.spacing.xl};
`;

const DistCard = styled(Card)`
  h3 {
    margin: 0 0 ${(p) => p.theme.spacing.md} 0;
    font-size: 0.95rem;
    font-weight: 700;
  }
`;

const DistRow = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr 48px;
  align-items: center;
  gap: 10px;
  padding: 4px 0;

  .d-label {
    font-size: 0.78rem;
    color: ${(p) => p.theme.colors.text.secondary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-transform: capitalize;
  }

  .d-bar {
    height: 14px;
    border-radius: 0 4px 4px 0;
    background: ${BAR_HUE};
    min-width: 2px;
    transition: width 0.3s ease;
  }

  .d-value {
    font-size: 0.8rem;
    font-weight: 600;
    color: ${(p) => p.theme.colors.text.secondary};
  }
`;

const FiltersCard = styled(Card)`
  margin-bottom: ${(p) => p.theme.spacing.xl};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${(p) => p.theme.spacing.md};
  margin-bottom: ${(p) => p.theme.spacing.md};
`;

const LogsCard = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 150px 150px 140px 130px minmax(280px, 1fr) 90px 120px;
  gap: ${(p) => p.theme.spacing.sm};
  align-items: center;
  padding: 10px ${(p) => p.theme.spacing.lg};
  border-bottom: 1px solid ${(p) => p.theme.colors.border.light};
  min-width: 1060px;

  &.head {
    background: ${(p) => p.theme.colors.surfaceAlt};
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.colors.text.secondary};
    position: sticky;
    top: 0;
    z-index: 1;
  }

  &.body {
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover {
      background: ${(p) => p.theme.colors.surfaceAlt};
    }

    &.trouble {
      background: #fdf3f1;

      &:hover { background: #fbeae7; }
    }

    &.open {
      background: ${(p) => p.theme.colors.primarySoft};
    }
  }
`;

const Cell = styled.div`
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.time {
    font-variant-numeric: tabular-nums;
    color: ${(p) => p.theme.colors.text.secondary};
  }

  &.mono {
    font-family: monospace;
    font-size: 0.78rem;
    color: ${(p) => p.theme.colors.text.secondary};
  }

  &.summary {
    white-space: normal;
    line-height: 1.35;
    color: ${(p) => p.theme.colors.text.secondary};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const ActionPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.7;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;

  ${(p) => {
    const map = {
      CREATE: '#2e7d5b',
      BULK_CREATE: '#2e7d5b',
      UPDATE: '#b26a12',
      BULK_UPDATE: '#b26a12',
      DELETE: '#c5372c',
      BULK_DELETE: '#c5372c',
      LOGIN: '#3a6ea5',
      LOGOUT: '#3a6ea5',
    };
    const c = map[p.action] || (String(p.action).startsWith('LARK_') ? '#3a6ea5' : '#6b645c');
    return `background: ${c}18; color: ${c}; border: 1px solid ${c}33;`;
  }}
`;

const DetailPanel = styled.div`
  padding: ${(p) => p.theme.spacing.lg};
  background: ${(p) => p.theme.colors.surfaceAlt};
  border-bottom: 1px solid ${(p) => p.theme.colors.border.light};

  .d-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: ${(p) => p.theme.spacing.md};
    margin-bottom: ${(p) => p.theme.spacing.md};
  }

  .d-item .k {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.colors.text.light};
    margin-bottom: 2px;
  }

  .d-item .v {
    font-size: 0.85rem;
    word-break: break-word;
  }

  .d-summary {
    font-size: 0.88rem;
    line-height: 1.5;
    margin-bottom: ${(p) => p.theme.spacing.md};
    white-space: pre-wrap;
  }

  .d-error {
    color: #c5372c;
    font-size: 0.85rem;
    margin-bottom: ${(p) => p.theme.spacing.md};
  }

  pre {
    background: ${(p) => p.theme.colors.surface};
    border: 1px solid ${(p) => p.theme.colors.border.light};
    border-radius: ${(p) => p.theme.borderRadius.medium};
    padding: ${(p) => p.theme.spacing.md};
    font-size: 0.75rem;
    line-height: 1.5;
    max-height: 260px;
    overflow: auto;
    margin: 4px 0 0 0;
  }
`;

const PagerBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${(p) => p.theme.spacing.md};
  padding: ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.lg};
  flex-wrap: wrap;

  .range {
    font-size: 0.85rem;
    color: ${(p) => p.theme.colors.text.secondary};
  }

  .pager-controls {
    display: flex;
    align-items: center;
    gap: ${(p) => p.theme.spacing.sm};
  }
`;

const CenterState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(p) => p.theme.spacing.md};
  padding: ${(p) => p.theme.spacing.xxl};
  color: ${(p) => p.theme.colors.text.secondary};
`;

const ErrorBox = styled.div`
  background: #fdf3f1;
  border: 1px solid #e7c9c3;
  color: #c5372c;
  padding: ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.lg};
  border-radius: ${(p) => p.theme.borderRadius.medium};
  margin-bottom: ${(p) => p.theme.spacing.lg};
`;

/* --------------------------- component ---------------------------- */

const EMPTY_FILTERS = {
  username: '',
  action: '',
  resource_type: '',
  status: '',
  start_date: '',
  end_date: '',
};

// datetime-local gives a naive local string; created_at is timestamptz, so
// send ISO-with-timezone or the filter shifts by the UTC offset.
const toApiParams = (f, limit, offset) => {
  const params = { limit, offset };
  if (f.username) params.username = f.username;
  if (f.action) params.action = f.action;
  if (f.resource_type) params.resource_type = f.resource_type;
  if (f.status) params.status = f.status;
  if (f.start_date) params.start_date = new Date(f.start_date).toISOString();
  if (f.end_date) params.end_date = new Date(f.end_date).toISOString();
  return params;
};

const formatDateTime = (s) => {
  const d = new Date(s);
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const JsonBlock = ({ label, value }) => {
  if (value == null) return null;
  let text;
  try {
    text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  } catch (e) {
    text = String(value);
  }
  return (
    <div className="d-item" style={{ gridColumn: '1 / -1' }}>
      <div className="k">{label}</div>
      <pre>{text}</pre>
    </div>
  );
};

const AuditLogsPage = () => {
  const { user } = useAuth();
  const [auditLogs, setAuditLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [summaryDays, setSummaryDays] = useState(7);
  const [availableActions, setAvailableActions] = useState([]);
  const [availableResources, setAvailableResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Draft = what the inputs show; applied = what the table currently uses.
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  const loadLogs = useCallback(async (filters, lim, off) => {
    setLoading(true);
    setError(null);
    try {
      const res = await auditAPI.getAuditLogs(toApiParams(filters, lim, off));
      setAuditLogs(res.data || []);
      setExpandedId(null);
    } catch (e) {
      console.error('Error loading audit logs:', e);
      setError('Failed to load audit logs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs(applied, limit, offset);
  }, [applied, limit, offset, loadLogs]);

  useEffect(() => {
    (async () => {
      try {
        const res = await auditAPI.getAuditSummary(summaryDays);
        setSummary(res.data);
      } catch (e) {
        console.error('Error loading audit summary:', e);
      }
    })();
  }, [summaryDays]);

  useEffect(() => {
    (async () => {
      try {
        const [actionsRes, resourcesRes] = await Promise.all([
          auditAPI.getAvailableActions(),
          auditAPI.getAvailableResources(),
        ]);
        setAvailableActions(actionsRes.data.actions || []);
        setAvailableResources(resourcesRes.data.resources || []);
      } catch (e) {
        console.error('Error loading audit filter options:', e);
      }
    })();
  }, []);

  const applyFilters = (next) => {
    setOffset(0);
    setApplied(next);
  };

  // Dropdowns apply immediately; text/date fields apply on Enter or Search.
  const handleDraftChange = (field, value, immediate = false) => {
    const next = { ...draft, [field]: value };
    setDraft(next);
    if (immediate) applyFilters(next);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    applyFilters(draft);
  };

  const handleClear = () => {
    setDraft(EMPTY_FILTERS);
    applyFilters(EMPTY_FILTERS);
  };

  const handleRefresh = () => {
    loadLogs(applied, limit, offset);
    setSummary(null);
    auditAPI.getAuditSummary(summaryDays).then((res) => setSummary(res.data)).catch(() => {});
  };

  if (!user || !['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor'].includes(user.role)) {
    return (
      <PageContainer>
        <ErrorBox>You don't have permission to view audit logs.</ErrorBox>
      </PageContainer>
    );
  }

  const topUser = summary
    ? Object.entries(summary.actions_by_user).sort(([, a], [, b]) => b - a)[0]
    : null;
  const topAction = summary
    ? Object.entries(summary.actions_by_type).sort(([, a], [, b]) => b - a)[0]
    : null;
  const successRate = summary && summary.total_actions > 0
    ? Math.round(((summary.actions_by_status.SUCCESS || 0) / summary.total_actions) * 100)
    : null;

  const actionDist = summary
    ? Object.entries(summary.actions_by_type).sort(([, a], [, b]) => b - a)
    : [];
  const resourceDist = summary
    ? Object.entries(summary.actions_by_resource).sort(([, a], [, b]) => b - a)
    : [];
  const actionMax = actionDist.length ? actionDist[0][1] : 0;
  const resourceMax = resourceDist.length ? resourceDist[0][1] : 0;

  const hasPrev = offset > 0;
  const hasNext = auditLogs.length === limit;

  return (
    <PageContainer>
      <HeaderCard>
        <div>
          <h1><FiFileText size={24} /> Audit Logs</h1>
          <p>Every change in the system, newest first — who did what, when, and from where.</p>
        </div>
        <HeaderControls>
          <Field>
            <label>Summary period</label>
            <Segmented>
              {SUMMARY_PERIODS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={summaryDays === d ? 'active' : ''}
                  onClick={() => setSummaryDays(d)}
                >
                  {d} days
                </button>
              ))}
            </Segmented>
          </Field>
          <ModernButton variant="secondary" onClick={handleRefresh} disabled={loading}>
            <FiRefreshCw size={16} /> Refresh
          </ModernButton>
        </HeaderControls>
      </HeaderCard>

      {summary && (
        <>
          <KpiGrid>
            <StatCard>
              <span className="value">{summary.total_actions.toLocaleString()}</span>
              <span className="label"><FiActivity size={13} style={{ verticalAlign: '-2px' }} /> Total actions</span>
              <span className="sub">last {summary.period.days} days</span>
            </StatCard>
            <StatCard accent={successRate == null ? undefined : successRate >= 99 ? '#2e7d5b' : '#b26a12'}>
              <span className="value">{successRate == null ? '—' : `${successRate}%`}</span>
              <span className="label"><FiTrendingUp size={13} style={{ verticalAlign: '-2px' }} /> Success rate</span>
              <span className="sub">
                {(summary.actions_by_status.FAILED || 0) + (summary.actions_by_status.ERROR || 0)} failed / error
              </span>
            </StatCard>
            <StatCard>
              <span className="value" title={topUser ? topUser[0] : ''}>{topUser ? topUser[0] : '—'}</span>
              <span className="label"><FiUser size={13} style={{ verticalAlign: '-2px' }} /> Most active user</span>
              <span className="sub">{topUser ? `${topUser[1].toLocaleString()} actions` : 'no activity'}</span>
            </StatCard>
            <StatCard>
              <span className="value" style={{ textTransform: 'capitalize' }}>{topAction ? prettyAction(topAction[0]).toLowerCase() : '—'}</span>
              <span className="label"><FiZap size={13} style={{ verticalAlign: '-2px' }} /> Top action</span>
              <span className="sub">{topAction ? `${topAction[1].toLocaleString()} times` : 'no activity'}</span>
            </StatCard>
          </KpiGrid>

          <DistGrid>
            <DistCard>
              <h3>Actions by type · last {summary.period.days} days</h3>
              {actionDist.map(([name, n]) => (
                <DistRow key={name}>
                  <span className="d-label" title={name}>{prettyAction(name).toLowerCase()}</span>
                  <div><div className="d-bar" style={{ width: `${actionMax ? Math.max(2, (n / actionMax) * 100) : 0}%` }} /></div>
                  <span className="d-value">{n.toLocaleString()}</span>
                </DistRow>
              ))}
              {actionDist.length === 0 && <div style={{ color: '#9c948a', fontSize: '0.85rem' }}>No activity in this period.</div>}
            </DistCard>
            <DistCard>
              <h3>Actions by resource · last {summary.period.days} days</h3>
              {resourceDist.map(([name, n]) => (
                <DistRow key={name}>
                  <span className="d-label" title={name}>{prettyAction(name).toLowerCase()}</span>
                  <div><div className="d-bar" style={{ width: `${resourceMax ? Math.max(2, (n / resourceMax) * 100) : 0}%` }} /></div>
                  <span className="d-value">{n.toLocaleString()}</span>
                </DistRow>
              ))}
              {resourceDist.length === 0 && <div style={{ color: '#9c948a', fontSize: '0.85rem' }}>No activity in this period.</div>}
            </DistCard>
          </DistGrid>
        </>
      )}

      <FiltersCard>
        <form onSubmit={handleSubmit}>
          <FiltersGrid>
            <Field>
              <label>Username</label>
              <ModernInput
                type="text"
                value={draft.username}
                onChange={(e) => handleDraftChange('username', e.target.value)}
                placeholder="Filter by username…"
              />
            </Field>
            <Field>
              <label>Action</label>
              <ModernSelect
                value={draft.action}
                onChange={(e) => handleDraftChange('action', e.target.value, true)}
              >
                <option value="">All actions</option>
                {availableActions.map((a) => <option key={a} value={a}>{a}</option>)}
              </ModernSelect>
            </Field>
            <Field>
              <label>Resource</label>
              <ModernSelect
                value={draft.resource_type}
                onChange={(e) => handleDraftChange('resource_type', e.target.value, true)}
              >
                <option value="">All resources</option>
                {availableResources.map((r) => <option key={r} value={r}>{r}</option>)}
              </ModernSelect>
            </Field>
            <Field>
              <label>Status</label>
              <ModernSelect
                value={draft.status}
                onChange={(e) => handleDraftChange('status', e.target.value, true)}
              >
                <option value="">All statuses</option>
                <option value="SUCCESS">Success</option>
                <option value="FAILED">Failed</option>
                <option value="ERROR">Error</option>
              </ModernSelect>
            </Field>
            <Field>
              <label>From</label>
              <ModernInput
                type="datetime-local"
                value={draft.start_date}
                onChange={(e) => handleDraftChange('start_date', e.target.value)}
              />
            </Field>
            <Field>
              <label>To</label>
              <ModernInput
                type="datetime-local"
                value={draft.end_date}
                onChange={(e) => handleDraftChange('end_date', e.target.value)}
              />
            </Field>
          </FiltersGrid>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <ModernButton type="submit"><FiSearch size={16} /> Search</ModernButton>
            <ModernButton type="button" variant="secondary" onClick={handleClear}>
              <FiFilter size={16} /> Clear filters
            </ModernButton>
          </div>
        </form>
      </FiltersCard>

      {error && <ErrorBox><FiAlertCircle size={16} style={{ verticalAlign: '-3px', marginRight: 6 }} />{error}</ErrorBox>}

      <LogsCard>
        <TableScroll>
          <Row className="head">
            <div>Time</div>
            <div>Action</div>
            <div>Resource</div>
            <div>User</div>
            <div>Summary</div>
            <div>Status</div>
            <div>IP address</div>
          </Row>

          {loading ? (
            <CenterState><LoadingSpinner size="36px" /><div>Loading audit logs…</div></CenterState>
          ) : auditLogs.length === 0 ? (
            <CenterState><FiClock size={28} /><div>No audit logs match these filters.</div></CenterState>
          ) : (
            auditLogs.map((log) => (
              <React.Fragment key={log.id}>
                <Row
                  className={`body ${log.status !== 'SUCCESS' ? 'trouble' : ''} ${expandedId === log.id ? 'open' : ''}`}
                  onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                  title="Click for details"
                >
                  <Cell className="time">{formatDateTime(log.created_at)}</Cell>
                  <Cell><ActionPill action={log.action}>{log.action}</ActionPill></Cell>
                  <Cell className="mono">{log.resource_type}{log.resource_id != null ? ` #${log.resource_id}` : ''}</Cell>
                  <Cell>{log.username || 'SYSTEM'}</Cell>
                  <Cell className="summary">{log.changes_summary || '—'}</Cell>
                  <Cell>
                    <Badge variant={log.status === 'SUCCESS' ? 'success' : log.status === 'FAILED' ? 'error' : 'warning'}>
                      {log.status}
                    </Badge>
                  </Cell>
                  <Cell className="mono">{log.ip_address || '—'}</Cell>
                </Row>

                {expandedId === log.id && (
                  <DetailPanel>
                    {log.changes_summary && <div className="d-summary">{log.changes_summary}</div>}
                    {log.error_message && (
                      <div className="d-error"><FiAlertCircle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />{log.error_message}</div>
                    )}
                    <div className="d-grid">
                      <div className="d-item"><div className="k">Full timestamp</div><div className="v">{new Date(log.created_at).toLocaleString()} ({log.created_at})</div></div>
                      <div className="d-item"><div className="k">Endpoint</div><div className="v">{log.api_endpoint || '—'}</div></div>
                      <div className="d-item"><div className="k">User</div><div className="v">{log.username || 'SYSTEM'}{log.user_id != null ? ` (id ${log.user_id})` : ''}</div></div>
                      <div className="d-item"><div className="k">User agent</div><div className="v">{log.user_agent || '—'}</div></div>
                      <JsonBlock label="Old values" value={log.old_values} />
                      <JsonBlock label="New values" value={log.new_values} />
                      <JsonBlock label="Additional data" value={log.additional_data} />
                    </div>
                  </DetailPanel>
                )}
              </React.Fragment>
            ))
          )}
        </TableScroll>

        <PagerBar>
          <span className="range">
            {auditLogs.length > 0
              ? `Showing ${(offset + 1).toLocaleString()}–${(offset + auditLogs.length).toLocaleString()} · newest first`
              : 'No results'}
          </span>
          <div className="pager-controls">
            <Field>
              <ModernSelect
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}
                style={{ width: 'auto' }}
              >
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / page</option>)}
              </ModernSelect>
            </Field>
            <ModernButton
              type="button"
              variant="secondary"
              disabled={!hasPrev || loading}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              ← Newer
            </ModernButton>
            <ModernButton
              type="button"
              variant="secondary"
              disabled={!hasNext || loading}
              onClick={() => setOffset(offset + limit)}
            >
              Older →
            </ModernButton>
          </div>
        </PagerBar>
      </LogsCard>
    </PageContainer>
  );
};

export default AuditLogsPage;
